import {Ease} from "../core/Animatable";
import {Container} from "../core/Container";
import {PointUtil} from "../core/Geom";
import {Callback, Mix} from "../core/Reflection";
import {Signal, Times} from "../core/Signal";
import {Assets} from "../data/Assets";
import {LocationCardType} from "../data/LocationCardType";
import {Match2Model, PickResult} from "../data/Match2Model";
import {voOptionsButtons} from "../data/VoiceOver";
import {AudioChannel} from "../managers/AudioChannel";
import {VoiceOverManager} from "../managers/VoiceOverManager";
import {Match2Card, Match2CardState} from "./Match2Card";
import {TransformBackup} from "./TransformBackup";

export class Match2Board<CardType extends LocationCardType> extends Mix(Container, TransformBackup) {

    public readonly cards: Match2Card[] = [];
    public readonly onComplete = Signal<Callback>();
    public readonly onMatch = Signal<(card1: Match2Card, card2: Match2Card) => void>();
    public readonly onPickCard = Signal<(card: Match2Card) => void>();

    private readonly _model: Match2Model<CardType>;
    private readonly _gap = {x: 0, y: 0};
    private _cardsEnabled = true;

    private readonly _wrongMatchesVO = ["patw_g02", "patw_g03", "patw_g04"];
    private _wrongMatches = 0;
    private _lockTurnCard = false;

    constructor(model: Match2Model<CardType>) {
        super();

        this._model = model;

        for (let i = 0; i < model.length; i++) {
            const card = new Match2Card(model.getCardAt(i), i);
            this.addChild(card);
            this.cards.push(card);
            card.onClick.add(() => {
                this.pickCard(card);
            });
            card.visible = false;
        }
    }

    get model(): Match2Model<CardType> {
        return this._model;
    }

    public show(cb?: Callback) {
        if (cb) {
            cb = Times(this.cards.length, cb);
        }
        let d = 0;
        for (const card of this.cards) {
            card.visible = true;
            card.moveXYFrom(0, 0, 0.3, Ease.backOut, {
                delay: d += 0.05,
                onComplete: cb,
            });
        }
    }

    public matchAll() {
        for (let i = 0; i < this.cards.length; i++) {
            const card1 = this.cards[i];
            card1.onClick(card1);
            for (let j = i + 1; j < this.cards.length; j++) {
                const card2 = this.cards[j];
                if (card1.cardType === card2.cardType) {
                    card2.onClick(card2);
                }
            }
        }
    }

    public getClosestEnabledCard(card: Match2Card) {
        return this.cards
            .filter((it) => it.enabled)
            .sort((a, b) => PointUtil.distance(a, card) - PointUtil.distance(b, card))[0];
    }

    public destroy() {
        this.onComplete.remove();
        this.onMatch.remove();
        super.destroy();
    }

    private getCardPositions(rows: number[]) {
        let j = 0;
        let k = 0;
        const card = this.cards[0];
        const length = rows.reduce((a, t) => a + t, 0);
        const positions = [];
        for (let i = 0; i < length; i++) {
            if (i >= j + rows[k]) {
                j += rows[k];
                k++;
            }
            const x = i - j;
            const y = k;
            positions.push({
                x: (x - rows[k] * 0.5) * (card.width + this._gap.x) + card.width * 0.5,
                y: (y - rows.length * 0.5) * (card.height + this._gap.y) + card.height * 0.5,
            });
        }
        return positions;
    }

    private pickCard(card: Match2Card) {
        if (card.isFlipping) {
            return;
        }
        if (this._lockTurnCard) {
            return;
        }
        this.onPickCard(card);
        const result = this._model.pickCard(card.cardIndex);
        const openCards = this._model.getOpenCards().map((it) => this.getCardAt(it.index));
        const isInOpenCards = openCards.indexOf(card) >= 0;
        if (isInOpenCards && (result === PickResult.Match || result === PickResult.NoMatch)) {
            this._lockTurnCard = true;
            openCards.forEach((it) => it.enabled = false);
        }
        card.flip(isInOpenCards ? () => {
            if (result === PickResult.Match) {
                this._wrongMatches = 0;
                this._lockTurnCard = false;
                openCards.forEach((it) => it.cardState = Match2CardState.Success);
                AudioChannel.SFX.play(Assets.audio.doMatch.assetName);
                this.onMatch(openCards[0], openCards[1]);
                if (this._model.isComplete) {
                    this.onComplete();
                }
            } else if (result === PickResult.NoMatch) {
                openCards.forEach((it) => it.cardState = Match2CardState.Failure);
                openCards.forEach((it, index) => it.shake(index === 0 ? -1 : 1));
                AudioChannel.SFX.play(Assets.audio.noMatch.assetName);

                this._wrongMatches++;
                if (this._wrongMatches >= 2) {
                    this._wrongMatches = 0;
                    VoiceOverManager.instance.playVO(this._wrongMatchesVO[0], voOptionsButtons);
                    this._wrongMatchesVO.push(this._wrongMatchesVO.shift()!);
                }

                this.delayedCall(0.8, () => {
                    this._lockTurnCard = false;
                    openCards.forEach((it) => {
                        if (it.isOpen) {
                            it.enabled = this._cardsEnabled;
                            it.flip(() => {
                                openCards.forEach((c) => c.cardState = Match2CardState.Default);
                            });
                        }
                    });
                });
            }
        } : undefined);
    }

    private getCardAt(index: number) {
        return this.cards[index];
    }

    get cardsEnabled(): boolean {
        return this._cardsEnabled;
    }

    set cardsEnabled(value: boolean) {
        if (this._cardsEnabled !== value) {
            this._cardsEnabled = value;
            for (const card of this.cards) {
                if (!card.isFlipping && card.cardState === Match2CardState.Default) {
                    card.enabled = value;
                }
            }
        }
    }
}
